import { useEffect, useMemo, useState } from 'react';

import { queryKeys, useApiQuery } from '../../api/hooks';
import type { BranchResponse } from '../../api/types';
import { useAuthStore } from '../../auth/authStore';

const STORAGE_KEY = 'laseera.branch';

/**
 * Which branch the operational screens are showing (spec §19: "branch picker pinned").
 *
 * Remembered across reloads, because a receptionist works at one branch all day and re-picking it
 * every morning is the kind of small friction that makes people distrust software. Remembered per
 * clinic, so a multi-clinic manager switching clinics does not land on a branch that no longer
 * exists.
 *
 * The choice is a display preference, never an authorisation: the API decides what a token may
 * reach, and a branch id in local storage grants nothing.
 *
 * Someone who works at one branch (a branch manager or receptionist, ASM-009) is offered only that
 * branch: the API answers "not found" for any other (CR-17), so offering them would only offer an
 * error.
 */
export function useBranchPicker() {
  const clinicId = useAuthStore((state) => state.activeClinicId);
  const ownBranchId = useAuthStore(
    (state) =>
      state.principal?.memberships?.find((membership) => membership.clinicId === state.activeClinicId)?.branchId ??
      null,
  );
  const branches = useApiQuery<BranchResponse[]>(
    queryKeys.branches(clinicId ?? ''),
    `/clinics/${clinicId}/branches`,
    { enabled: Boolean(clinicId) },
  );
  const all = branches.data;
  const offered = useMemo(
    () => (all && ownBranchId ? all.filter((branch) => branch.id === ownBranchId) : all),
    [all, ownBranchId],
  );

  const [branchId, setBranchId] = useState<string | null>(null);

  useEffect(() => {
    if (!clinicId || !offered) {
      return;
    }
    const active = offered.filter((branch) => branch.status === 'ACTIVE');
    const candidates = active.length > 0 ? active : offered;
    const remembered = window.localStorage.getItem(`${STORAGE_KEY}.${clinicId}`);

    setBranchId((current) => {
      const stillThere = (id: string | null) =>
        id !== null && candidates.some((branch) => branch.id === id);
      if (stillThere(current)) {
        return current;
      }
      if (stillThere(remembered)) {
        return remembered;
      }
      return candidates[0]?.id ?? null;
    });
  }, [clinicId, offered]);

  const choose = (id: string) => {
    setBranchId(id);
    if (clinicId) {
      window.localStorage.setItem(`${STORAGE_KEY}.${clinicId}`, id);
    }
  };

  return {
    clinicId,
    branchId,
    branches: offered ?? [],
    isLoading: branches.isLoading,
    choose,
  };
}
